import { useComputed } from "@suse-o11y/signal";
import uniqBy from "lodash/uniqBy";
import without from "lodash/without";
import React from "react";

import { pluralize } from "~/tools/pluralize";

import {
	Api,
	type MonitorCheckStatus,
	type MonitorCheckStatusRelatedFailures,
	type MonitorCheckStatusRelatedFailuresCheckStatus,
} from "../../../../common/openapi";
import { Widget, WidgetBody, WidgetHeader } from "../../../../components/_widget";
import { Empty } from "../../../../components/react/empty/Empty";
import { NoData } from "../../../../components/react/empty/EmptyPlaceholder";
import { GenericErrorMessage } from "../../../../components/react/error/GenericErrorMessage";
import { LabeledSwitch } from "../../../../components/react/filter/LabeledSwitch";
import { LoadingSpinner } from "../../../../components/react/loading-spinner/LoadingSpinner";
import { InlineLink } from "../../../../components/react/styled/StyledComponents";
import { useQueryMonitorCheckStatusRelatedFailures } from "../../../../queries";

import { useMonitorStatusDrawerContext } from "./MonitorStatusDrawer/use-monitor-status-drawer-context";
import { MonitorRelatedIssuesGraph } from "./MonitorRelatedIssuesGraph";
import type { CheckStateChain } from "./MonitorStatusDrawer";

const pluralizeComponent = pluralize({
	1: "1 impacted component",
	more: (count) => `${count} impacted components`,
});

interface MonitorRelatedIssuesProps {
	checkState: MonitorCheckStatus;
	checkStateChain: CheckStateChain;
}

export const MonitorRelatedIssues: React.FC<MonitorRelatedIssuesProps> = ({ checkStateChain, checkState }) => {
	const { timestamp } = useMonitorStatusDrawerContext();
	const chainedCheckStateValue = useComputed((monitor) => checkStateChain.peek(monitor));
	const showImpactedComponent = useComputed(chainedCheckStateValue.showImpactedComponent);

	const {
		data: { filteredData = [], impactedDataLength = 0 } = {},
		isLoading,
		error,
	} = useQueryMonitorCheckStatusRelatedFailures<{
		impactedDataLength: number;
		filteredData: Array<MonitorCheckStatusRelatedFailuresCheckStatus>;
	}>(
		{
			identifier: checkState.identifier,
			timestamp: useComputed(timestamp),
		},
		{
			select: React.useCallback(
				({ checkStatuses }: MonitorCheckStatusRelatedFailures) => {
					const impactedData = checkStatuses.filter(
						({ topology }) => topology.relation === Api.MonitorCheckStatusRelatedFailuresTopology.RelationEnum.IMPACTED,
					);
					const impactedDataLength = uniqBy(impactedData, "component.id").length;

					return {
						impactedDataLength,
						filteredData:
							showImpactedComponent || impactedDataLength === 0
								? checkStatuses
								: without(checkStatuses, ...impactedData),
					};
				},
				[showImpactedComponent],
			),
		},
	);

	return (
		<Widget card>
			<WidgetHeader
				help="The related health violations are calculated by using the dependencies and following them. This can be helpful to quickly get to the root of a particular issue."
				size="lg"
				title="Causing and impacted components"
			>
				{impactedDataLength > 0 && (
					<span className="font-normal text-xs leading-[22px]">
						<LabeledSwitch
							checked={showImpactedComponent}
							className="ml-2"
							label={`Include impacted components (${impactedDataLength})`}
							onChange={(checked) => chainedCheckStateValue.showImpactedComponent.write(checked)}
							size="small"
						/>
					</span>
				)}
			</WidgetHeader>

			<WidgetBody>
				{isLoading ? (
					<LoadingSpinner className="my-4" />
				) : error ? (
					<GenericErrorMessage error={error} />
				) : filteredData.length === 0 ? (
					impactedDataLength > 0 ? (
						<NoData>
							There are no causing components, but there are related health violations on
							<br />
							{pluralizeComponent(impactedDataLength)}.{" "}
							<InlineLink onClick={() => chainedCheckStateValue.showImpactedComponent.write(true)}>
								Include impacted components
							</InlineLink>
						</NoData>
					) : (
						<Empty />
					)
				) : (
					<MonitorRelatedIssuesGraph
						currentCheckState={checkState}
						data={filteredData}
						onMessageClick={(nextCheckStateId) => checkStateChain.push(nextCheckStateId)}
					/>
				)}
			</WidgetBody>
		</Widget>
	);
};
